// Mesquite Mafia: Hometown Zero - Master Application Entry Point
import './styles/index.css';

import { initCapacitor, isNative, hapticImpact, hapticNotification } from './native/capacitor.js';
import { initPWA, showPWAInstallPrompt, triggerPWAInstall } from './pwa/pwa.js';

import {
  sfxMuted, bgmMuted, sfxVolume, bgmVolume, bgmPlaylist, currentBGMIndex, sfxMap,
  playSFX, SFX, loadBGMTrack, startBGM, toggleBGM, nextBGMTrack, toggleSFX, updateMusicUI, playVoiceBark
} from './core/audio.js';

import {
  state, log, escapeHtml, updateCurrencies, addXP, updateXPUI,
  currentActiveDirective, getActiveDirective, updateHUDDirective,
  claimActiveHUDDirective, executeDirectiveAction, switchTab, handleStartGate,
  enterGame, switchStartScreenVideo
} from './core/state.js';

import {
  spawnBulletHole, triggerGunAnimation, playActionSequence, initPointerKinematics
} from './core/kinematics.js';

import {
  SYNDICATE_CHARACTERS, selectedDossierId, playerProfile, initPlayerProfile,
  selectCharacterDossier, renderCharacterDossier, openPlayerProfile, savePlayerProfile,
  randomizeBossHandle, selectArchetypeCard, selectAvatarCard, challengeBossFromDossier
} from './dossiers/characters.js';

import {
  tutorialSelectedArchetype, tutorialSelectedAvatar, tutorialSelectedRole, setTutorialStep,
  selectTutorialArchetype, selectTutorialAvatar, randomizeTutorialHandle,
  submitTutorialStep1, finishTutorial, openTutorialModal
} from './tutorial/tutorial.js';

import {
  triggerCompoundAction, upgradeCompound, updateCompoundUI, updateSectorLockVisuals,
  fortifyIronGarrison, bribePoliceHeat
} from './engine/compound.js';

import {
  updateMissionProgress, claimMission, renderMissions, hydrateMissionsFromSaved
} from './engine/missions.js';

import {
  sectorData, selectedSector, selectSector, updateMapNodes, reinforceSector,
  dispatchMarch, toggleWorldMapView, currentMapZoomPreset, initLiveMapInteractions,
  setMapZoomPreset, zoomMapStep, focusSectorOnMap, dispatchSupplyConvoy, deployRoadblock,
  sendDecoyRunner, updateLiveMapVehicles, update4XTelemetry, isTacticalOverlayVisible,
  toggleTacticalOverlay, toggleOverlayTransparency, triggerRandomWorldEvent,
  warTable, init3DWarTable, resize3DWarTable, onWarTablePointerMove, onWarTableClick,
  update3DSelectionHighlight, update3DRoadblocks, set3DCameraPreset, reset3DCamera
} from './engine/world.js';

import {
  getHeroMaxHp, getHeroBaseAtk, getHeroBaseShield, selectedBoss, selectBoss,
  showDamage, updateCombatUI, decrementCooldowns, executeTurn, showRewardsPopup,
  closeRewards, resetBattle, setCombatStance, updateTacticalMatchup,
  triggerHighwayPursuit, executePursuitAction, closeHighwayPursuit,
  scannerIntelOptions, openScannerIntelModal, closeScannerIntelModal,
  currentIncursionRival, respondToIncursion, closeIncursionModal, aiTick
} from './engine/combat.js';

import {
  updateStartScreenState, promptBossLogin, initCloudflareDB, fallbackOffline,
  syncStateToD1, openPlayerSignupModal, closePlayerSignupModal, callServerAction
} from './services/api.js';

import {
  buyJewelry, giftAssociate, buyStorePack, listAssetForSale, pawnAsset,
  buyMarketListing, buyTacticalAirdrop, stripeStoreConfig, initStoreConfig,
  openVenmoStoreModal, closeVenmoStoreModal, initiateStorePurchase, checkPaymentReturnStatus
} from './services/store.js';

// Expose all public functions to window for HTML onclick / inline attribute compatibility
Object.assign(window, {
  // Audio & SFX
  playSFX, SFX, startBGM, toggleBGM, nextBGMTrack, toggleSFX, loadBGMTrack, updateMusicUI, playVoiceBark,
  
  // State & Nav
  state, log, escapeHtml, updateCurrencies, addXP, updateXPUI, switchTab, handleStartGate,
  enterGame, switchStartScreenVideo, getActiveDirective, updateHUDDirective,
  claimActiveHUDDirective, executeDirectiveAction,

  // Kinematics & VFX
  spawnBulletHole, triggerGunAnimation, playActionSequence, initPointerKinematics,

  // Characters & Dossiers
  SYNDICATE_CHARACTERS, selectedDossierId, playerProfile, initPlayerProfile,
  selectCharacterDossier, renderCharacterDossier, openPlayerProfile, savePlayerProfile,
  randomizeBossHandle, selectArchetypeCard, selectAvatarCard, challengeBossFromDossier,

  // Tutorial
  tutorialSelectedArchetype, tutorialSelectedAvatar, tutorialSelectedRole, setTutorialStep,
  selectTutorialArchetype, selectTutorialAvatar, randomizeTutorialHandle,
  submitTutorialStep1, finishTutorial, openTutorialModal,

  // Compound
  triggerCompoundAction, upgradeCompound, updateCompoundUI, updateSectorLockVisuals,
  fortifyIronGarrison, bribePoliceHeat,

  // Missions
  updateMissionProgress, claimMission, renderMissions, hydrateMissionsFromSaved,

  // World & 3D War Table
  sectorData, selectedSector, selectSector, updateMapNodes, reinforceSector,
  dispatchMarch, toggleWorldMapView, currentMapZoomPreset, initLiveMapInteractions,
  setMapZoomPreset, zoomMapStep, focusSectorOnMap, dispatchSupplyConvoy, deployRoadblock,
  sendDecoyRunner, updateLiveMapVehicles, update4XTelemetry, isTacticalOverlayVisible,
  toggleTacticalOverlay, toggleOverlayTransparency, triggerRandomWorldEvent,
  warTable, init3DWarTable, resize3DWarTable, set3DCameraPreset, reset3DCamera,

  // Combat & Encounters
  getHeroMaxHp, getHeroBaseAtk, getHeroBaseShield, selectedBoss, selectBoss,
  showDamage, updateCombatUI, decrementCooldowns, executeTurn, showRewardsPopup,
  closeRewards, resetBattle, setCombatStance, updateTacticalMatchup,
  triggerHighwayPursuit, executePursuitAction, closeHighwayPursuit,
  openScannerIntelModal, closeScannerIntelModal, respondToIncursion, closeIncursionModal, aiTick,

  // API & Sync
  updateStartScreenState, promptBossLogin, initCloudflareDB, fallbackOffline,
  syncStateToD1, openPlayerSignupModal, closePlayerSignupModal, callServerAction,

  // Store & Payments
  buyJewelry, giftAssociate, buyStorePack, listAssetForSale, pawnAsset,
  buyMarketListing, buyTacticalAirdrop, stripeStoreConfig, initStoreConfig,
  openVenmoStoreModal, closeVenmoStoreModal, initiateStorePurchase, checkPaymentReturnStatus,

  // PWA & Native
  showPWAInstallPrompt, triggerPWAInstall, hapticImpact, hapticNotification, isNative
});

// Primary System Initialization Routine
document.addEventListener('DOMContentLoaded', () => {
  initPlayerProfile();
  initCloudflareDB();
  initPointerKinematics();
  initLiveMapInteractions();
  initStoreConfig();
  checkPaymentReturnStatus();
  updateCurrencies();
  updateMapNodes();
  updateCompoundUI();
  renderMissions();
  updateHUDDirective();
  update4XTelemetry();
  updateTacticalMatchup();
  initPWA();
  initCapacitor();

  if (window.CombatEngine) CombatEngine.init();
  if (window.AlliancesEngine) AlliancesEngine.init();

  setInterval(aiTick, 4000);
  setInterval(updateLiveMapVehicles, 100);
  setTimeout(() => {
    init3DWarTable();
  }, 150);

  console.log('[MESQUITE MAFIA]: 2.0.0 Architecture initialized (PWA + Capacitor + Cloudflare Workers D1).');
});
